import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

const DB_NAME = 'dws-query-cache'
const STORE_NAME = 'query-client'
const KEY = 'persistedClient'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function getItem(db: IDBDatabase): Promise<PersistedClient | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const request = store.get(KEY)
    request.onsuccess = () => resolve(request.result as PersistedClient | undefined)
    request.onerror = () => reject(request.error)
  })
}

function setItem(db: IDBDatabase, value: PersistedClient): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const request = store.put(value, KEY)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

function deleteItem(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const request = store.delete(KEY)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

export function createIDBPersister(): Persister {
  let dbPromise: Promise<IDBDatabase> | undefined

  function getDB() {
    if (!dbPromise) {
      dbPromise = openDB()
    }
    return dbPromise
  }

  return {
    persistClient: async (client: PersistedClient) => {
      const db = await getDB()
      await setItem(db, client)
    },
    restoreClient: async () => {
      const db = await getDB()
      return await getItem(db)
    },
    removeClient: async () => {
      const db = await getDB()
      await deleteItem(db)
    },
  }
}
